import { Component, ChangeDetectorRef, EventEmitter, inject, Output, OnDestroy } from '@angular/core';
import { PageContainerComponent } from '../../../layout/page-container/page-container.component';
import { TenureAdjustmentFilterSchema, TenureAdjustmentTableSchema, TenureAdjustmentTitle, TenureAdjustmentProps, validateSpinSearchConfig, AdjustedTenureSearchConfig, AdjustmentTenureUpdateSearchConfig, AdjustmentAttchmentSearchConfig, AdjustedTenureUpdateForm, AdjustedTenureFilterForm, AdjustmentTenureTableTitle, TenureAdjustmentResetActionName, } from '../../../model/tenure/tenure-adjustment.model';
import { DataTableSchema } from '../../../model/dynamic-data-table.model';
import { FieldNames } from '../../../model/form-fields.model';
import { ButtonFieldConfig, DynamicFormArray, FIELD_TYPES, FormFieldConfig, Props, } from '../../../model/form.model';
import { CommonService } from '../../../services/common.service';
import { BaseComponent } from '../../base/base.component';
import { FormService } from '../../../services/form.service';
import { PaginatorService } from '../../../services/paginator-service.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, ExportConfig, SearchConfig } from '../../../model/api.model';
import { CommonUtils } from '../../../utils/common.utils';
import { ConfirmationDialogComponent } from '../../../layout/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { successFailureMsg } from '../../../model/tenure.model';
import { CommonModule } from '@angular/common';
import { TenureAttachmentDisplayComponent } from '../../tenure-attachment-display/tenure-attachment-display.component';
import { BUTTON_NAMES } from '../../../model/common.model';
import { EXCEL_EXTENSION } from '../../../services/excel-export.service';
import { StaffMasterExportSearchConfig } from '../../../model/staff-master.model';

type callFor = 'List' | 'Create' | 'Edit' | 'Delete';

@Component({
  selector: 'app-tenure-adjustment',
  standalone: true,
  imports: [CommonModule, PageContainerComponent],
  providers: [PaginatorService],
  templateUrl: './tenure-adjustment.component.html',
  styleUrl: './tenure-adjustment.component.scss',
})
export class TenureAdjustmentComponent extends BaseComponent implements OnDestroy {
  protected override data: string = TenureAdjustmentTitle;
  public title: string = TenureAdjustmentTitle;
  protected override filterSchema: DynamicFormArray = structuredClone(TenureAdjustmentFilterSchema);
  public override dataTableSchema: DataTableSchema = (() => {
    const schema = structuredClone({ ...TenureAdjustmentTableSchema, headerActions: [] }) as DataTableSchema;
    schema.headerActions = [...(TenureAdjustmentTableSchema.headerActions ?? [])];
    return schema;
  })();
  protected override props: Props = TenureAdjustmentProps;
  readonly destroy$ = new Subject<void>();
  currentPage: any;
  isFirstLoad = true;
  isDataInitialized = false;
  tableFilter = '';
  paginatorService = inject(PaginatorService);
  changeRef = inject(ChangeDetectorRef);
  public beginRender = false;
  private adjustmentForm!: FormGroup;
  private adjustmentUpdateForm!: FormGroup;
  private currentSpinDetails: any = {};
  private readonly subscribedUpdateForms = new WeakSet<FormGroup>();

  private readonly service = inject(CommonService)
  private readonly formService = inject(FormService);
  private readonly commonUtils = inject(CommonUtils);
  public readonly dialog = inject(MatDialog)
  public isDialogOpen = true

  @Output() dataEmitter = new EventEmitter<boolean>();

  override ngOnInit(): void {
    this.onResetClicked();
    this.runCommonLogic();
    this.configureSpinIdSearchAction();
    this.formService.form$.subscribe((form: any) => {
      if (form['formName']) {
        if (form['formName'] === AdjustedTenureUpdateForm) {
          this.adjustmentUpdateForm = form;
        } else if (form['formName'] === AdjustedTenureFilterForm) {
          this.adjustmentForm = form;
          if (Object.keys(this.currentSpinDetails).length <= 0) {
            this.setSpinIdFieldDisabled(false);
            this.setSpinIdSearchVisible(true);
            this.setSpinIdResetVisible(false);
          }
        }
      }
      this.dynamicFieldValidation(form, form['formName']);
    });
    this.dataTableSchema.filterCallback = this.paginationFilterCallback
    this.dataTableSchema.exportCallback = this.exportCallback
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false; 
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] =
          this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      }
      else{
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        const searchConfig = this.getSearchConfig();
        this.fetchAllData(searchConfig);
      }
    });
    this.assignCallbacks();
    this.beginRender = true;
  }

  private configureSpinIdSearchAction() {
    const spinIdField = this.filterSchema.FieldsArray.find((field) => field.fieldName === FieldNames['SpinId']);
    if (!spinIdField) {
      return;
    }
    spinIdField.fieldActionConfig = {
      icon: 'search',
      click: (spinId: string) => this.onSpinIdSearchClick(spinId),
      resetClick: () => this.onResetClicked(),
      resetVisible: false,
      searchVisible: true,
    };
  }

  private setSpinIdSearchVisible(visible: boolean) {
    const spinIdField = this.filterSchema.FieldsArray.find((field) => field.fieldName === FieldNames['SpinId']);
    if (!spinIdField?.fieldActionConfig) {
      return;
    }
    spinIdField.fieldActionConfig.searchVisible = visible;
  }

  private setSpinIdResetVisible(visible: boolean) {
    const spinIdField = this.filterSchema.FieldsArray.find((field) => field.fieldName === FieldNames['SpinId']);
    if (!spinIdField?.fieldActionConfig) {
      return;
    }
    spinIdField.fieldActionConfig.resetVisible = visible;
  }


  private setSpinIdFieldDisabled(_disabled: boolean) {
    const spinIdControls = this.adjustmentForm?.controls[FieldNames['SpinId']] as FormControl;
    const spinIdField = this.filterSchema.FieldsArray.find((field) => field.fieldName === FieldNames['SpinId']);
    if (spinIdField) {
      spinIdField['disabled'] = false;
    }
    if (spinIdControls?.disabled) {
      spinIdControls?.enable({ emitEvent: false });

    }
  }

  private applySpinIdDefaultState() {
    this.setSpinIdFieldDisabled(false);
    this.setSpinIdSearchVisible(true);
    this.setSpinIdResetVisible(false);
  }

  private onSpinIdSearchClick(spinId: string) {
    const selectedSpinId = spinId?.toString()?.trim() ?? '';
    const spinIdControl = this.adjustmentForm?.get(FieldNames['SpinId']);
    if (!selectedSpinId) {
      spinIdControl?.setErrors({ customError: true, msg: 'Please enter SpinId' });
      spinIdControl?.markAsTouched();
      return;
    }
    const spinIdField = this.filterSchema.FieldsArray.find((field) => field.fieldName === FieldNames['SpinId']);
    if (spinIdField) {
      spinIdField.selectedValue = selectedSpinId;
    }
    spinIdControl?.setValue(selectedSpinId, { emitEvent: false });
    this.onSearchClicked();
  }
  disableFields = (disableFields: Array<any>) => {
    if (this.adjustmentUpdateForm) {
      const criteriaDependentFields: Set<string> = new Set([FieldNames['Brand'], FieldNames['DealerCode'], FieldNames['JobCode']])
      this.filterSchema.AdditionalForms?.FieldsArray.forEach(field => {
        if (criteriaDependentFields.has(field['fieldName'])) {
          const control = this.adjustmentUpdateForm?.get(field['fieldName']);
          if (disableFields.includes(field['fieldName'])) {
            field['required'] = false;
            field['disabled'] = true;
            field['selectedValue'] = ''
            if (control) {
              control.clearValidators();
              control.setValue('', { emitEvent: false });
              control.disable({ emitEvent: false })
            }
          } else {
            field['required'] = true;
            field['disabled'] = false;
            if (field['options']?.length === 1) {
              field['selectedValue'] = field['options'][0]['fieldValue']
            }
            if (control) {
              control.setValidators(Validators.required);
              control.enable({ emitEvent: false })
              control.setValue(field['selectedValue'] ?? '', { emitEvent: false });
            }
          }
          control?.updateValueAndValidity();
        }

      })
    }

  }
  private handleTypeChange(value: any) {  
    if (!this.filterSchema.AdditionalForms?.FieldsArray) {
      return;
    }
    if (!this.adjustmentUpdateForm) {
      return;
    }
    const jobField = this.filterSchema.AdditionalForms.FieldsArray.find(x => x.fieldName === FieldNames['JobCode'])
    const dealer = this.filterSchema.AdditionalForms.FieldsArray.find(x => x.fieldName === FieldNames['DealerCode'])
    const getDealers = (tenureType: 'dealer' | 'job') => {
      if (tenureType === 'dealer') {
        return Array.from(new Map(this.currentSpinDetails['dealerCodes']
          .map((x: { [x: string]: any; }) => [x['dealerCode'], x])).values())
          .map((code: any) => ({ fieldValue: code['dealerCode'], fieldLabel: `${code['dealerCode']} - ${code['dealerName']}` }));
      } else {
        if (this.currentSpinDetails.jobCodes) {
          return Array.from(new Map(this.currentSpinDetails['jobCodes']
            .map((x: { [x: string]: any; }) => [x['dealerCode'], x])).values())
            .map((code: any) => ({ fieldValue: code['dealerCode'], fieldLabel: `${code['dealerCode']} - ${code['dealerName']}` }));
        }
        return []
      }
    }
    if (value === 'brand' || value === 'oem') {
      this.disableFields([FieldNames.DealerCode, FieldNames.JobCode])
    } else if (value === 'dealer' && dealer) {
      dealer['options'] = getDealers(value)
      if (dealer['options']?.length === 1) {
        dealer['selectedValue'] = dealer['options'][0]['fieldValue'];
      }
      this.disableFields([FieldNames.Brand, FieldNames.JobCode])
    } else if (value === 'job' && dealer) {
      dealer['options'] = getDealers(value)
      this.disableFields([FieldNames.Brand])
      if (dealer['options']?.length === 1) {
        dealer['selectedValue'] = dealer['options'][0]['fieldValue'];
      }
      if (jobField) {
        if (dealer['selectedValue'] === '') {
          jobField['disabled'] = true;
          this.adjustmentUpdateForm?.get(FieldNames.JobCode)?.disable({ emitEvent: false })
        } else {
          jobField['disabled'] = false;
          this.adjustmentUpdateForm?.get(FieldNames.JobCode)?.enable({ emitEvent: false })
        }
        jobField['selectedValue'] = '';
      }
      }
      this.handleFormValueChanges(this.adjustmentUpdateForm);
  }

  private handleDealerCodeChange(value: any, form: FormGroup) {
    if (!this.filterSchema.AdditionalForms?.FieldsArray) {
      return;
    }
    const formValues = form.getRawValue();
    const dynamicFields: Record<string, FormFieldConfig> = {};
    this.filterSchema.AdditionalForms.FieldsArray.forEach(field => {
      if (field['fieldName'] === FieldNames['DealerCode']) {
        dynamicFields[FieldNames['DealerCode']] = field
      } else if (field['fieldName'] === FieldNames['JobCode']) {
        dynamicFields[FieldNames['JobCode']] = field
      } else if (field['fieldName'] === FieldNames['Brand']) {
        dynamicFields[FieldNames['Brand']] = field
      }
    })
    const jobField = this.filterSchema.AdditionalForms.FieldsArray.find(x => x.fieldName === FieldNames['JobCode'])
    if (jobField) {
      if (formValues[FieldNames.Type] === 'job' && ![undefined, null, ''].includes(value)) {
        const jobsForDealers = (this.currentSpinDetails['jobCodes'] ?? [])
          .filter((x: any) => x.dealerCode === formValues[FieldNames['DealerCode']])
        const uniqJobsForDealers = Array.from(new Map(jobsForDealers
          .map((x: { [x: string]: any; }) => [x['jobCode'], x])).values())
        const options = uniqJobsForDealers.map((code: any) => ({ fieldValue: code['jobCode'], fieldLabel: `${code['jobCode']} - ${code['jobName']}` }))
        jobField['options'] = options;
        if (jobField['options']?.length === 1) {
          jobField['selectedValue'] = jobField['options'][0]['fieldValue'];
        }
        jobField['disabled'] = false;
      } else {
        dynamicFields[FieldNames['JobCode']]['options'] = [];
        jobField['disabled'] = true;
        dynamicFields[FieldNames['JobCode']]['selectedValue'] = ''
      }
    }
    this.handleFormValueChanges(form);
  }

  private handleFormValueChanges(form: FormGroup) {
    if (!this.filterSchema.AdditionalForms?.FieldsArray) {
      return;
    }
    if (Object.keys(this.currentSpinDetails).length > 0) {
      let tenureValue = '';
      const formValues = form.getRawValue()
      if ((formValues[FieldNames.Type] === 'brand' || formValues[FieldNames.Type] === 'oem') && !['', null, undefined].includes(formValues[FieldNames.Brand])) {
        tenureValue = this.currentSpinDetails?.brands?.find((x: { brand: any; }) => (x.brand === formValues[FieldNames.Brand]))?.['brandTenure'] ?? '';
      } else if (formValues[FieldNames.Type] === 'dealer' && !['', null, undefined].includes(formValues[FieldNames.DealerCode])) {
        tenureValue = this.currentSpinDetails?.dealerCodes?.find((x: { dealerCode: any; }) => x.dealerCode === formValues[FieldNames.DealerCode])?.['dealerTenure'] ?? '';
      } else if (formValues[FieldNames.Type] === 'job' && !['', null, undefined].includes(formValues[FieldNames.JobCode])) {
        tenureValue = this.currentSpinDetails?.jobCodes?.find((x: { jobCode: any; dealerCode: any; }) => x.jobCode === formValues[FieldNames.JobCode] && x.dealerCode === formValues[FieldNames.DealerCode])?.['jobTenure'] ?? '';
      }
      this.filterSchema.AdditionalForms.FieldsArray.forEach(field => {
        if (field.fieldName === FieldNames['ActualTenure']) {
          field['selectedValue'] = tenureValue
          form.get(FieldNames.ActualTenure)?.setValue(tenureValue, { emitEvent: false });
        }
      })
    }
  }

  dynamicFieldValidation(form: FormGroup, formName: string) {
    if (formName === AdjustedTenureUpdateForm) {
      if (this.subscribedUpdateForms.has(form)) {
        return;
      }
      this.subscribedUpdateForms.add(form);
      form.get(FieldNames.Type)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.handleTypeChange(value);
      });
      form.get(FieldNames.DealerCode)?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.handleDealerCodeChange(value, form);
      });
      form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.handleFormValueChanges(form);
      });
    }
    
  }

  showAttachment = (event: any) => {
    const dialogRef = this.dialog.open(TenureAttachmentDisplayComponent, {
      autoFocus: true,
      panelClass: 'tenure-attachment-dialog',
      data: {
        spinId: event['spinId'],
        attachments: event[FieldNames['Attachment']]
      }
    });

    const instance = dialogRef.componentInstance;
    instance.closeCliked.subscribe(() => {
      dialogRef.close()
    })
  }

  private configureAttachmentColumnAction() {
    this.dataTableSchema.columnSchema.forEach((col) => {
      if (col['fieldKey'] === 'tenureAttachments' && col['actionConfig']) {
        col['actionConfig'][0]['onClick'] = this.showAttachment;
      }
    });
  }

  private configureAdditionalFormActions() {
    if (!this.filterSchema.AdditionalForms) {
      return;
    }
    this.filterSchema.AdditionalForms.onButtonClicked = (btn: { name: string }) => {
      if (btn['name'] === BUTTON_NAMES['Adjust']) {
        this.createbtnOnClick();
      } else if (btn['name'] === BUTTON_NAMES['Reset']) {
        this.resetUpdateForm();
      }
    };
  }

  private configureHeaderResetAction() {
    if (!this.dataTableSchema.headerActions?.length) {
      return;
    }
    this.dataTableSchema.headerActions.forEach((action) => {
      if (action['fieldKey'] === TenureAdjustmentResetActionName) {
        action['onClick'] = () => {
          this.onResetClicked();
        };
      }
    });
  }

  assignCallbacks() {
    this.configureAttachmentColumnAction();
    this.configureAdditionalFormActions();
    this.configureHeaderResetAction();
  }
  callTenureApi(searchParam: SearchConfig, successFailureMsg?: successFailureMsg) {
    this.setTableLoadingState(true);
    return this.service.getDataFromDataSource(searchParam, ApiEnvNames['Tenure']).then(response => {
      if (successFailureMsg) {
        if (response['status'] === API_STATUS['Success']) {
          this.commonUtils.showMatSnackBar(successFailureMsg['success'])
        } else {
          this.commonUtils.showMatSnackBar(successFailureMsg['error'])
        }
      }
      this.setTableLoadingState(false);
      return response?.result ?? []
    }).catch(err => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      this.setTableLoadingState(false);
      return []
    })
  }
  override onResetClicked = () => {
    this.dataTableSchema.isExportRequired = true;
    this.dataTableSchema.isSearchRequired = true;
    this.dataTableSchema.tableTitle = AdjustmentTenureTableTitle;
    this.adjustmentForm?.reset();
    this.resetUpdateForm()
    this.dataTableSchema.inputFilter = ''
    this.currentSpinDetails = {};
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) =>
      ({ ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '', disabled: false })
    );
    this.applySpinIdDefaultState();
    if (this.filterSchema.AdditionalForms) {
      this.filterSchema.AdditionalForms.isEnabled = false
      this.filterSchema.AdditionalForms.FieldsArray.forEach((field) => {
        if (([FieldNames['Brand'], FieldNames['DealerCode'], FieldNames['JobCode']] as any).includes(field['fieldName'])) {
          field['options'] = [];
        }
        field['selectedValue'] = field['defaultValue'] ?? '';
        field['disabled'] = true;
        this.adjustmentUpdateForm?.get(field['fieldName'])?.disable({ emitEvent: false })
      })
      this.filterSchema.AdditionalForms.ButtonsArray = this.filterSchema.AdditionalForms.ButtonsArray
        .map((btn: ButtonFieldConfig) => ({ ...btn, isDisabled: true }))
    }
    this.filterSchema.ButtonsArray?.forEach(btn => {
      if (btn['name'] === BUTTON_NAMES['Search']) {
        btn['isDisabled'] = false;
      }
      if(btn.detectChanges){
          btn.detectChanges()
      }
    })
    this.disableEnableButtons(true);
    this.checkForAnyEmptyFields();
    this.isDataInitialized = false;
    this.resetDataTable();
    this.resetSorting();
    const searchConfig = this.getSearchConfig();
    this.fetchAllData(searchConfig);
  }

  override  onSearchClicked = async () => {
    if (this.adjustmentForm.invalid) {
      this.adjustmentForm.markAllAsTouched()
      return;
    }
    const spinIdControls = this.adjustmentForm?.controls[FieldNames['SpinId']] as FormControl;
    const spinId = spinIdControls.value;
    if (spinId.length !== 12) {
      spinIdControls.setErrors({ customError: true, msg: 'Spin ID should be of 12 Digits' });
      return
    }
    const searchConfig: SearchConfig = structuredClone(validateSpinSearchConfig);
    searchConfig['body'] = {
      spinId: spinId
    }
    this.dataTableSchema.dataSource = []
    this.dataTableSchema.tableTitle = 'Search Results for Spin ID: '+spinId;
    this.dataTableSchema.isExportRequired = true;
    this.dataTableSchema.isSearchRequired = false;
    const result = await this.callTenureApi(searchConfig)
    if (Array.isArray(result) && result.length > 0) {
      const data = result[0];
      this.currentSpinDetails = data;
      this.updateFormOptions(data);
      if (this.filterSchema.AdditionalForms) {
        this.filterSchema.AdditionalForms.ButtonsArray = this.filterSchema.AdditionalForms.ButtonsArray
          .map((btn: ButtonFieldConfig) => ({ ...btn, isDisabled: false }))
        this.filterSchema.AdditionalForms.isEnabled = true
      }
      this.filterSchema.FieldsArray.forEach(field => {
        field['disabled'] = true
      })
      this.resetDataTable();
      this.resetSorting();
      this.setSpinIdFieldDisabled(true);
      this.setSpinIdSearchVisible(false);
      this.setSpinIdResetVisible(true);
      this.fetchAllData(this.getSearchConfig());
    } else {
      spinIdControls.setErrors({ customError: true, msg: 'Please enter a valid SpinId' });
    }
  };

  backToTenure() {
    this.dataEmitter.emit();
  }

  async fetchAllLayoutData(payload?: any) {
    const searchconfig = structuredClone(AdjustedTenureSearchConfig)
    searchconfig['body'] = payload;
    this.dataTableSchema.dataSource = []
    const result = await this.callTenureApi(searchconfig);
    this.dataTableSchema.dataSource = result.map(data => {
      if (data[FieldNames['Attachment']] && typeof data[FieldNames['Attachment']] === 'string') {
        data[FieldNames['Attachment']] = JSON.parse(data[FieldNames['Attachment']])
      }
      data['attachmentCount'] = data[FieldNames['Attachment']]?.length ?? 0
      return data
    })
  }

  updateFormOptions(data: any) {
    const spinInDependentField: Array<string> = [FieldNames['ActualTenure']]; 
    const defaultTenureCritiriaFields: Array<string> = [FieldNames.DealerCode, FieldNames.JobCode]; 
    const formData: any = {};
    this.filterSchema.AdditionalForms?.FieldsArray.forEach(field => {
      field['selectedValue'] = field['defaultValue'] ?? '';

      if (field['fieldName'] === FieldNames['Brand'] && data['brands']) {
        field['options'] = Array.from(new Set(data['brands']
          .map((x: any) => x['brand'])))
          .map(code => ({ fieldValue: code, fieldLabel: code }));
        if (field['options']?.length === 1) {
          field['selectedValue'] = field['options'][0]['fieldValue'];
        }
      }

      if (field['fieldName'] === FieldNames['ActualTenure']) {
        if (formData[FieldNames['Type']] === 'brand' && ![null, undefined, ''].includes(formData[FieldNames['Brand']])) {
          field['selectedValue'] = this.currentSpinDetails?.brands.find((x: { brand: any; }) => (x.brand === formData[FieldNames.Brand]))['brandTenure'];
        }
      }

      if (!spinInDependentField.includes(field['fieldName']) && !defaultTenureCritiriaFields.includes(field['fieldName'])) {
        field['disabled'] = false;
        this.adjustmentUpdateForm?.controls[field['fieldName']]?.enable();
      }

      if (defaultTenureCritiriaFields.includes(field['fieldName'])) {
        field['required'] = false;
      }

      formData[field['fieldName']] = field['selectedValue'];

      // Update form control value without emitting events to avoid triggering validations
      this.adjustmentUpdateForm?.get(field['fieldName'])?.setValue(field['selectedValue'], { emitEvent: false });
    });

    this.adjustmentUpdateForm?.updateValueAndValidity({ emitEvent: false });

    this.filterSchema.AdditionalForms?.FieldsArray.forEach(field => {
      field['isLoading'] = false;
    });

    this.disableFields([FieldNames.DealerCode, FieldNames.JobCode]);
  }

  createbtnOnClick = () => {
    if (this.adjustmentUpdateForm?.invalid) {
      this.adjustmentUpdateForm?.markAllAsTouched()
      return;
    }
    if (Object.keys(this.currentSpinDetails).length <= 0) {
      this.commonUtils.showMatSnackBar('Please filter Spin ID before adjusting tenure')
      return;
    }

    const adjustedValue = Number.parseInt(this.adjustmentUpdateForm?.get('adjustedTenure')?.value || '0');
    const actualTenureField = this.filterSchema.AdditionalForms?.FieldsArray.find(field => field.fieldName === 'actualTenure');
    const actualValue = Number.parseInt(actualTenureField?.selectedValue || '0');

    if ((actualValue + adjustedValue) < 0) {
      this.commonUtils.showMatSnackBar('Final tenure cannot be negative');
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        message: "Are you sure, you want to adjust the Tenure?",
        header: "Confirm Tenure Adjustment"
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commonUtils.spinner('show');
        this.createAndSaveAttachment()
      } else {
        this.commonUtils.spinner('hide');
      }
      dialogRef.close()
    });
  }

  createAndSaveAttachment = async () => {
    try {
    let attachmentData: any = null;
    const fieldData: any = {};
    const payload: any = {};
    this.filterSchema.FieldsArray.forEach(field => {
      payload[field['fieldName']] = fieldData[field['fieldName']] = field['selectedValue'];
    })
    this.filterSchema.AdditionalForms?.FieldsArray.forEach(field => {
      if (field['fieldName'] === FieldNames['Attachment']) {
        attachmentData = field['selectedValue'];
      } else {
        payload[field['fieldName']] = fieldData[field['fieldName']] = field['selectedValue'];
      }
    })
    if (this.service.loggedUserDetails) {
      payload['createdBy'] = this.service.loggedUserDetails['workdayId'];
      payload['createdByName'] = this.service.loggedUserDetails['userName'];
      payload['updatedBy'] = this.service.loggedUserDetails['workdayId'];
      payload['updatedByName'] = this.service.loggedUserDetails['userName']
    }
    if (payload[FieldNames['Type']] === 'oem') {
      payload['isOem'] = true;
      payload[FieldNames['Type']] = 'brand'
    } else {
      payload['isOem'] = false;
    }
    const successFailureMsg: successFailureMsg = {
      [API_STATUS['Success']]: 'Tenure Details Updated Successfully',
      [API_STATUS['Error']]: 'Error On Updaing Tenure Details'
    }
    const searchConfig: SearchConfig = structuredClone(AdjustmentTenureUpdateSearchConfig);
    searchConfig['body'] = payload;
    this.setTableLoadingState(true);
    const response = await this.callTenureApi(searchConfig, successFailureMsg);
    if (response) {
      if (response?.[0] && attachmentData) {
        const result = response[0]
        const formData: FormData = new FormData();
        if (Array.isArray(attachmentData)) {
          attachmentData.forEach(data => {
            formData.append(FieldNames['Attachment'], data);
          })
        } else {
          formData.append(FieldNames['Attachment'], attachmentData);
        }
        formData.append(FieldNames['SpinId'], result['spin_id'])
        formData.append(FieldNames['Brand'], result[FieldNames['Brand']])
        formData.append(FieldNames['DealerCode'], result[FieldNames['DealerCode']])
        formData.append(FieldNames['JobCode'], payload[FieldNames['JobCode']])
        formData.append(FieldNames['Type'], payload[FieldNames['Type']])
        formData.append('tenureId', result[`${payload[FieldNames['Type']]}_tenure_history_id`])
        const attachmentSearchConfig: SearchConfig = structuredClone(AdjustmentAttchmentSearchConfig);
        attachmentSearchConfig['body'] = formData;
        const attachmentSuccessFailureMsg = {
          [API_STATUS['Success']]: 'Tenure Attachment uploaded successfully',
          [API_STATUS['Error']]: 'Tenure Attachment uploaded failed, Please try again after sometime'
        }
        await this.callTenureApi(attachmentSearchConfig, attachmentSuccessFailureMsg);
      }
    }
    this.resetUpdateForm()
      await this.onSearchClicked()
    } finally {
      this.commonUtils.spinner('hide');
    }
  }

  resetUpdateForm = () => {
    this.updateFormOptions(this.currentSpinDetails);

    if (this.adjustmentUpdateForm) {
      // Reset form state to pristine and untouched to prevent validation errors showing immediately
      this.adjustmentUpdateForm.markAsPristine();
      this.adjustmentUpdateForm.markAsUntouched();

      // Clear all validation errors explicitly
      Object.keys(this.adjustmentUpdateForm.controls).forEach(key => {
        this.adjustmentUpdateForm.get(key)?.setErrors(null);
      });
    }
  };

  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };

  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true
    this.fetchAllData(searchConfig, true)
  };

  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  };

  getSearchConfig = () => {
    const searchConfig = structuredClone(AdjustedTenureSearchConfig)
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if(Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])){
          searchConfig['body'][field['fieldName']] = field['selectedValue']
        }else{
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = '';
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if (this.sortBy && Object.keys(this.sortBy).length > 0) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig
  };

  fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    const searchConfig = structuredClone(searchParam)
    const body = searchConfig['body'] || {};
    searchConfig['body'] = body;
    if (!callFromExport) {
      searchConfig['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchConfig['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    const exportQueryConfig = this.getExportConfig()
    const exportConfig: ExportConfig = {
      tableSchema: this.dataTableSchema,
      environment: ApiEnvNames['StaffMaster'],
      fileExtension: EXCEL_EXTENSION,
      searchParameters: StaffMasterExportSearchConfig,
      exportFileName: 'Tenure_Adjustment',
    };
    if (callFromExport) {
      searchConfig['body']['exportConfig'] = exportQueryConfig
    }
    this.service.getDataFromDataSource(searchConfig, ApiEnvNames['Tenure']).then(async response => {
           if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
            if(callFromExport){
              await this.service.downloadExcelFromS3(response.result[0],exportConfig);
            }
            else{
              this.dataTableSchema['pageSizeOptions']['pageLength'] =response?.result?.[0]?.totalRecordsCount ?? 0;
              this.dataTableSchema.dataSource = this.handleResponse(response.result);
              if (!this.isDataInitialized && response.result.length > 0) {
                this.isDataInitialized = true;
              }
            }
          } else if(callFromExport){
             await this.service.recursiveCallS3(exportQueryConfig, exportConfig);
          }
          else{
            this.dataTableSchema.dataSource = [];
            this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
          }
        this.changeRef.detectChanges()
    }).catch(err => {      
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    }).finally(() => {
      this.setTableLoadingState(false);
    })
  };

  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          pageIndex: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }

  handleResponse(response: any): any[] {
    return response.map((item: any) => {
      const formattedItem = { ...item };
      if (formattedItem[FieldNames['Attachment']] && typeof formattedItem[FieldNames['Attachment']] === 'string') {
        formattedItem[FieldNames['Attachment']] = JSON.parse(formattedItem[FieldNames['Attachment']]);
      }
      formattedItem['attachmentCount'] = formattedItem[FieldNames['Attachment']]?.length ?? 0;
      return formattedItem;
    });
  }

  getExportConfig() {
    return {
      fileName: `Tenure_Adjustment_${this.commonUtils.getFormattedDate()}.csv`,
      bucketName: this.commonUtils.getBucketName()
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
